import { useEffect, useMemo, useRef, useState } from "react";
import { attachmentDownloadUrl, benefitsClient, logoUrl } from "../apiClient/benefitsClient";
import type { BenefitInput } from "../apiClient/benefitsClient";
import { ApiError } from "../../platform/apiClient/apiRequest";
import { NotifyAllControl } from "../../platform/components/NotifyControls";
import type { Benefit, BenefitPatch } from "../types/benefit.types";

interface FormState {
  category: string;
  merchant: string;
  offerText: string;
  details: string;
  imageUrl: string;
  validFrom: string;
  validTo: string;
}

const EMPTY_FORM: FormState = {
  category: "",
  merchant: "",
  offerText: "",
  details: "",
  imageUrl: "",
  validFrom: "",
  validTo: "",
};

function toPatch(form: FormState): BenefitPatch {
  return {
    category: form.category.trim(),
    merchant: form.merchant.trim(),
    offerText: form.offerText.trim(),
    details: form.details.trim() || null,
    imageUrl: form.imageUrl.trim() || null,
    validFrom: form.validFrom || null,
    validTo: form.validTo || null,
  };
}

function toInput(form: FormState): BenefitInput {
  return {
    category: form.category.trim(),
    merchant: form.merchant.trim(),
    offerText: form.offerText.trim(),
    details: form.details.trim() || undefined,
    imageUrl: form.imageUrl.trim() || undefined,
    validFrom: form.validFrom || null,
    validTo: form.validTo || null,
  };
}

function benefitToForm(b: Benefit): FormState {
  return {
    category: b.category,
    merchant: b.merchant,
    offerText: b.offerText,
    details: b.details ?? "",
    imageUrl: b.imageUrl ?? "",
    validFrom: b.validFrom ?? "",
    validTo: b.validTo ?? "",
  };
}

function BenefitForm({
  form,
  setForm,
  onSubmit,
  submitting,
  error,
  submitLabel,
}: {
  form: FormState;
  setForm: (f: FormState) => void;
  onSubmit: () => void;
  submitting: boolean;
  error: string | null;
  submitLabel: string;
}) {
  return (
    <div className="form">
      <div className="form-field">
        <label>Category</label>
        <input
          type="text"
          placeholder="e.g. Electronics, Fashion, Travel"
          value={form.category}
          onChange={(e) => setForm({ ...form, category: e.target.value })}
        />
      </div>
      <div className="form-field">
        <label>Merchant</label>
        <input
          type="text"
          placeholder="e.g. B.Tech"
          value={form.merchant}
          onChange={(e) => setForm({ ...form, merchant: e.target.value })}
        />
      </div>
      <div className="form-field">
        <label>Offer</label>
        <input
          type="text"
          placeholder="e.g. 10% discount, Triple 0% installment"
          value={form.offerText}
          onChange={(e) => setForm({ ...form, offerText: e.target.value })}
        />
      </div>
      <div className="form-field">
        <label>Details (optional)</label>
        <textarea
          rows={3}
          placeholder="Terms, promo codes, branch locations, contact info…"
          value={form.details}
          onChange={(e) => setForm({ ...form, details: e.target.value })}
        />
      </div>
      <div className="form-field">
        <label>Image URL (optional)</label>
        <input
          type="text"
          placeholder="/benefits/vendor-rates.jpeg"
          value={form.imageUrl}
          onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
        />
        <span className="field-hint">Shows the vendor&rsquo;s own graphic as-is instead of retyped text.</span>
      </div>
      <div className="form-row">
        <div className="form-field">
          <label>Valid from (optional)</label>
          <input type="date" value={form.validFrom} onChange={(e) => setForm({ ...form, validFrom: e.target.value })} />
        </div>
        <div className="form-field">
          <label>Valid to (optional)</label>
          <input type="date" value={form.validTo} onChange={(e) => setForm({ ...form, validTo: e.target.value })} />
          <span className="field-hint">Leave blank for an ongoing deal. Expired deals drop off the employee view automatically.</span>
        </div>
      </div>

      {error && <div className="form-banner form-banner--error">{error}</div>}

      <div className="form-actions">
        <button type="button" className="btn btn-primary" onClick={onSubmit} disabled={submitting}>
          {submitting ? "Saving…" : submitLabel}
        </button>
      </div>
    </div>
  );
}

const MAX_ATTACHMENT_MB = 10;

function AttachmentControl({ benefit, onChanged }: { benefit: Benefit; onChanged: () => void }) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleFileChosen(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file later (e.g. after a failed attempt)
    if (!file) return;

    if (file.size > MAX_ATTACHMENT_MB * 1024 * 1024) {
      setError(`File is too large — the limit is ${MAX_ATTACHMENT_MB}MB.`);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await benefitsClient.uploadAttachment(benefit.id, file);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't upload this file.");
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove(attachmentId: string) {
    setBusy(true);
    setError(null);
    try {
      await benefitsClient.removeAttachment(attachmentId);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't remove this attachment.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="benefit-attachment-control">
      <input ref={fileInputRef} type="file" hidden onChange={handleFileChosen} />
      {benefit.attachments.length > 0 && (
        <ul className="benefit-attachment-list">
          {benefit.attachments.map((a) => (
            <li key={a.id} className="benefit-attachment-existing">
              <a href={attachmentDownloadUrl(a.id)} target="_blank" rel="noopener">
                📎 {a.fileName}
              </a>
              <button type="button" className="btn btn-danger-ghost" onClick={() => handleRemove(a.id)} disabled={busy}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      <button type="button" className="btn btn-ghost" onClick={() => fileInputRef.current?.click()} disabled={busy}>
        {busy ? "Uploading…" : benefit.attachments.length > 0 ? "Add another file" : "Attach file"}
      </button>
      {error && <span className="field-error">{error}</span>}
    </div>
  );
}

const MAX_LOGO_MB = 2;

function LogoControl({ benefit, onChanged }: { benefit: Benefit; onChanged: () => void }) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleFileChosen(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Logo must be an image file.");
      return;
    }
    if (file.size > MAX_LOGO_MB * 1024 * 1024) {
      setError(`Logo is too large — the limit is ${MAX_LOGO_MB}MB.`);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await benefitsClient.uploadLogo(benefit.id, file);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't upload this logo.");
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove() {
    setBusy(true);
    setError(null);
    try {
      await benefitsClient.removeLogo(benefit.id);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't remove this logo.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="benefit-attachment-control">
      <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleFileChosen} />
      {benefit.logoBlobName ? (
        <span className="benefit-attachment-existing">
          <img className="benefit-admin-logo-preview" src={logoUrl(benefit.id)} alt="" />
          <button type="button" className="btn btn-ghost" onClick={() => fileInputRef.current?.click()} disabled={busy}>
            Replace logo
          </button>
          <button type="button" className="btn btn-danger-ghost" onClick={handleRemove} disabled={busy}>
            Remove
          </button>
        </span>
      ) : (
        <button type="button" className="btn btn-ghost" onClick={() => fileInputRef.current?.click()} disabled={busy}>
          {busy ? "Uploading…" : "Add logo"}
        </button>
      )}
      {error && <span className="field-error">{error}</span>}
    </div>
  );
}

function BenefitRow({ benefit, onChanged }: { benefit: Benefit; onChanged: () => void }) {
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState<FormState>(() => benefitToForm(benefit));
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isExpired = Boolean(benefit.validTo && benefit.validTo < new Date().toISOString().slice(0, 10));

  async function handleSaveEdit() {
    setSubmitting(true);
    setError(null);
    try {
      await benefitsClient.updateBenefit(benefit.id, toPatch(form));
      setIsEditing(false);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't save changes.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleToggleActive() {
    setSubmitting(true);
    setError(null);
    try {
      await benefitsClient.updateBenefit(benefit.id, { isActive: !benefit.isActive });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't update status.");
      setSubmitting(false);
    }
  }

  async function handleDelete() {
    setSubmitting(true);
    setError(null);
    try {
      await benefitsClient.deleteBenefit(benefit.id);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't delete this benefit.");
      setSubmitting(false);
      setConfirmingDelete(false);
    }
  }

  if (isEditing) {
    return (
      <li className="benefit-admin-row benefit-admin-row--editing">
        <BenefitForm form={form} setForm={setForm} onSubmit={handleSaveEdit} submitting={submitting} error={error} submitLabel="Save" />
        <div className="form-actions">
          <button type="button" className="btn btn-ghost" onClick={() => setIsEditing(false)} disabled={submitting}>
            Cancel
          </button>
        </div>
      </li>
    );
  }

  return (
    <li className="benefit-admin-row">
      {benefit.imageUrl && <img className="benefit-admin-thumb" src={benefit.imageUrl} alt={`${benefit.merchant} offer`} />}
      <div className="benefit-admin-info">
        <span className="benefit-merchant">{benefit.merchant}</span>
        <span className="benefit-offer">{benefit.offerText}</span>
        {benefit.details && <p className="benefit-details">{benefit.details}</p>}
        {(benefit.validFrom || benefit.validTo) && (
          <span className="benefit-validity">
            {benefit.validFrom ? `From ${benefit.validFrom}` : ""}
            {benefit.validFrom && benefit.validTo ? " · " : ""}
            {benefit.validTo ? `Until ${benefit.validTo}` : ""}
          </span>
        )}
        <LogoControl benefit={benefit} onChanged={onChanged} />
        <AttachmentControl benefit={benefit} onChanged={onChanged} />
      </div>

      <span className={`status-badge ${!benefit.isActive || isExpired ? "status-badge--no-show" : "status-badge--attended"}`}>
        {!benefit.isActive ? "Inactive" : isExpired ? "Expired" : "Active"}
      </span>

      {confirmingDelete ? (
        <div className="cancel-confirm">
          <span>Delete?</span>
          <button type="button" className="btn btn-ghost" onClick={() => setConfirmingDelete(false)} disabled={submitting}>
            No
          </button>
          <button type="button" className="btn btn-danger" onClick={handleDelete} disabled={submitting}>
            {submitting ? "Deleting…" : "Yes"}
          </button>
        </div>
      ) : (
        <div className="coach-actions">
          <button type="button" className="btn btn-ghost" onClick={() => setIsEditing(true)} disabled={submitting}>
            Edit
          </button>
          <button type="button" className="btn btn-ghost" onClick={handleToggleActive} disabled={submitting}>
            {benefit.isActive ? "Deactivate" : "Reactivate"}
          </button>
          <button type="button" className="btn btn-danger-ghost" onClick={() => setConfirmingDelete(true)} disabled={submitting}>
            Delete
          </button>
        </div>
      )}
      <NotifyAllControl
        subjectDefault={`New benefit: ${benefit.merchant}`}
        messageDefault={`Check out a new benefit — ${benefit.merchant}: ${benefit.offerText}.`}
      />
      {error && <span className="field-error">{error}</span>}
    </li>
  );
}

export function BenefitsAdminPage() {
  const [benefits, setBenefits] = useState<Benefit[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  function refresh() {
    setIsLoading(true);
    setError(null);
    benefitsClient
      .getAdminBenefits()
      .then(setBenefits)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load benefits."))
      .finally(() => setIsLoading(false));
  }

  useEffect(refresh, []);

  async function handleCreate() {
    setCreating(true);
    setCreateError(null);
    try {
      await benefitsClient.createBenefit(toInput(form));
      setForm(EMPTY_FORM);
      refresh();
    } catch (err) {
      setCreateError(err instanceof ApiError ? err.message : "Couldn't add this benefit.");
    } finally {
      setCreating(false);
    }
  }

  const byCategory = useMemo(() => {
    const map = new Map<string, Benefit[]>();
    for (const b of benefits) {
      const list = map.get(b.category) ?? [];
      list.push(b);
      map.set(b.category, list);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [benefits]);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Manage Benefits</h1>
          <p className="page-sub">Add, edit, deactivate, or remove deals in the employee catalog.</p>
        </div>
      </header>

      <div className="form-card">
        <h2>Add a benefit</h2>
        <BenefitForm form={form} setForm={setForm} onSubmit={handleCreate} submitting={creating} error={createError} submitLabel="Add benefit" />
      </div>

      <h2 className="admin-section-heading">All benefits</h2>
      {error ? (
        <div className="state-panel">
          <p>{error}</p>
        </div>
      ) : isLoading ? (
        <div className="state-panel">Loading…</div>
      ) : byCategory.length === 0 ? (
        <div className="state-panel">
          <p>No benefits yet — add the first one above.</p>
        </div>
      ) : (
        <div className="benefits-categories">
          {byCategory.map(([category, items]) => (
            <section className="benefits-category" key={category}>
              <h2 className="benefits-category-title">{category}</h2>
              <ul className="bookings-day-items benefit-admin-list">
                {items.map((b) => (
                  <BenefitRow key={b.id} benefit={b} onChanged={refresh} />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
